import { useEffect, useReducer, useState, useRef, type ChangeEvent, type KeyboardEvent, useMemo } from 'react';
import { Typography } from '@mui/material';
import { Input } from '@/UI/Input/Input';
import { FlexCenVer } from '@/UI/styles';

type DurationField = 'min' | 'sec' | 'frames';

const MINUTES_SECONDS_MAX = 59;

interface DurationInputProps {
  secDuration: number;
  fps: number;
  onChange: (value: number) => void;
}

type DurationState = Record<DurationField, string>;

type MinMaxValues = Record<DurationField, { min: number; max: number }>;

type DurationAction =
  | { type: 'SET_FIELD'; field: DurationField; value: string }
  | { type: 'SET_ALL'; min: string; sec: string; frames: string }
  | { type: 'RESET_FIELD'; field: DurationField };

const durationReducer = (state: DurationState, action: DurationAction): DurationState => {
  switch (action.type) {
    case 'SET_FIELD':
      return { ...state, [action.field]: action.value };
    case 'SET_ALL':
      return {
        min: action.min,
        sec: action.sec,
        frames: action.frames,
      };
    case 'RESET_FIELD':
      return { ...state, [action.field]: '00' };
    default:
      return state;
  }
};

export const DurationInput = ({ secDuration, fps, onChange }: DurationInputProps) => {
  const [duration, dispatch] = useReducer(durationReducer, {
    min: '',
    sec: '',
    frames: '',
  });
  const [isFocused, setIsFocused] = useState(false);

  const minutesInputRef = useRef<HTMLInputElement>(null);
  const secondsInputRef = useRef<HTMLInputElement>(null);
  const framesInputRef = useRef<HTMLInputElement>(null);

  const minMaxValues: MinMaxValues = useMemo(() => {
    return {
      min: { min: 0, max: MINUTES_SECONDS_MAX },
      sec: { min: 0, max: MINUTES_SECONDS_MAX },
      frames: { min: 0, max: fps - 1 },
    };
  }, [fps]);

  useEffect(() => {
    const totalSeconds = Math.floor(secDuration);
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    const frms = Math.floor((secDuration % 1) * fps);

    dispatch({
      type: 'SET_ALL',
      min: mins.toString().padStart(2, '0'),
      sec: secs.toString().padStart(2, '0'),
      frames: frms.toString().padStart(2, '0'),
    });
  }, [secDuration, fps]);

  const handleChange = (field: DurationField, value: string) => {
    dispatch({ type: 'SET_FIELD', field, value });
  };

  const handleBlur = (field: DurationField) => {
    let numValue = parseInt(duration[field], 10);

    const minMax = minMaxValues[field];
    if (isNaN(numValue) || numValue < minMax.min) {
      numValue = minMax.min;
    }

    if (numValue > minMax.max) {
      numValue = minMax.max;
    }

    let min = parseInt(duration.min, 10);
    let sec = parseInt(duration.sec, 10);
    let frames = parseInt(duration.frames, 10);

    if (field === 'min') {
      min = numValue;
    } else if (field === 'sec') {
      sec = numValue;
    } else if (field === 'frames') {
      frames = numValue;
    }

    const totalSeconds = min * 60 + sec + frames / fps;
    // we do the actual duration change only on blur
    onChange(totalSeconds);
    if (totalSeconds === secDuration) {
      // we trigger update to fields because we might changed the value but the duration stayed the same
      // we still want to fix the input value. The duration prop won't change and won't trigger the update
      dispatch({
        type: 'SET_ALL',
        min: min.toString().padStart(2, '0'),
        sec: sec.toString().padStart(2, '0'),
        frames: frames.toString().padStart(2, '0'),
      });
    }
    setIsFocused(false);
  };

  const focusInput = (ref: React.RefObject<HTMLInputElement | null>) => {
    if (ref.current) {
      const inputElement = ref.current.querySelector('input');
      if (inputElement) {
        inputElement.focus();
      }
    }
  };

  const adjustFieldValue = (field: DurationField, delta: 1 | -1) => {
    const currentValue = parseInt(duration[field], 10) || 0;
    const minMax = minMaxValues[field];
    const newValue = currentValue + delta;

    if (newValue < minMax.min || newValue > minMax.max) {
      return;
    }

    const paddedValue = newValue.toString().padStart(2, '0');
    dispatch({ type: 'SET_FIELD', field, value: paddedValue });
  };

  const handleKeyDown = (field: DurationField, e: KeyboardEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    const cursorPosition = input.selectionStart ?? 0;
    const cursorEndPosition = input.selectionEnd ?? 0;
    const valueLength = input.value.length;

    if (e.key === 'Enter') {
      e.preventDefault();
      input.blur();
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      adjustFieldValue(field, 1);
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      adjustFieldValue(field, -1);
      return;
    }

    if (e.key === 'ArrowRight' && cursorPosition === valueLength && cursorPosition === cursorEndPosition) {
      e.preventDefault();
      if (field === 'min') {
        focusInput(secondsInputRef);
      } else if (field === 'sec') {
        focusInput(framesInputRef);
      }
    }

    if (e.key === 'ArrowLeft' && cursorPosition === 0 && cursorPosition === cursorEndPosition) {
      e.preventDefault();
      if (field === 'sec') {
        focusInput(minutesInputRef);
      } else if (field === 'frames') {
        focusInput(secondsInputRef);
      }
    }
  };

  return (
    <FlexCenVer
      sx={{
        '& > .MuiFormControl-root:first-of-type .MuiInputBase-root': {
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0,
          borderRight: 'none',
          pr: 0,
        },
        '& > .MuiFormControl-root:nth-of-type(2) .MuiInputBase-root': {
          borderRadius: 0,
          borderLeft: 'none',
          borderRight: 'none',
          px: 0,
        },
        '& > .MuiFormControl-root:nth-of-type(3) .MuiInputBase-root': {
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0,
          borderLeft: 'none',
          pl: 0,
        },
        ...(isFocused && {
          '& .MuiInputBase-root': {
            borderColor: 'var(--input-focus-border-color)',
          },
        }),
      }}
    >
      <Input
        ref={minutesInputRef}
        size="small"
        value={duration.min}
        onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange('min', e.target.value)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => handleBlur('min')}
        sx={{ width: 30 }}
        inputProps={{
          type: 'text',
          maxLength: 2,
          onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => handleKeyDown('min', e),
        }}
      />
      <Typography variant="body-sm-md" sx={{ position: 'relative', zIndex: 1, left: -4 }}>
        :
      </Typography>
      <Input
        ref={secondsInputRef}
        size="small"
        value={duration.sec}
        onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange('sec', e.target.value)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => handleBlur('sec')}
        inputProps={{
          type: 'text',
          maxLength: 2,
          onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => handleKeyDown('sec', e),
        }}
        sx={{ position: 'relative', left: -3, width: 20 }}
      />
      <Typography variant="body-sm-md" sx={{ position: 'relative', zIndex: 1, left: -6 }}>
        :
      </Typography>
      <Input
        ref={framesInputRef}
        size="small"
        value={duration.frames}
        onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange('frames', e.target.value)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => handleBlur('frames')}
        inputProps={{
          type: 'text',
          maxLength: 2,
          style: { textAlign: 'left' },
          onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => handleKeyDown('frames', e),
        }}
        sx={{ position: 'relative', left: -6, width: 60 }}
      />
    </FlexCenVer>
  );
};
